import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import type { IModuleDefinition, IModuleStrings } from '@platform/core/types';
import { childModuleType } from '@platform/engine/menu/childModuleType';
import { findModuleDefinition } from '@platform/modularity/moduleDefinitionRegistry';
import { useMeQuery } from '@platform/state/api';
import type { ICustomizeModulePageProps } from '../ICustomizeModulePageProps';
import { customizeCrumbs } from './customizeCrumbs';
import { formRecordType } from './formRecordType';
import { formSingular } from './formSingular';
import type { ICustomizeHead } from './ICustomizeHead';
import type { ISettingsLinks } from './ISettingsLinks';

/**
 * Шапка сторінки модуля в «Налаштуваннях» (прототип v4 cust-form / cust-module без inMod) — спільна для готової сторінки
 * (CustomizeModuleBody) і сторінки, що вантажиться (SettingsLoadingPage): тип форми з `?type=` чи дочірня таблиця з `?focus=`, назва
 * («Форма «Справа»», модуль чи таблиця), крихти «Простір › Налаштування › Форми / Модулі (на рівні «Глобально» — Каталог модулів) ›
 * Сторінка» і повернення «← Форми» / «← Модулі». Рядки — `strings` (пакет модуля чи лише підписи визначення).
 */
export function useCustomizeHead(ws: string, moduleKey: string, section: ICustomizeModulePageProps['section'], global: boolean,
  module: IModuleDefinition | undefined, strings: IModuleStrings): ICustomizeHead {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { data: me } = useMeQuery();
  const forms = section === 'forms';
  const rt = module && formRecordType(module, forms ? params.get('type') : null);
  const child = !forms && module ? childModuleType(module, params.get('focus')) : undefined;
  const moduleName = module ? strings.moduleName(module.module.name) : '';
  // Підрядок глобальної форми називає модуль опублікованою назвою (прототип: mm.name шаблону), а не глобальною.
  const formName = global ? strings.moduleName(findModuleDefinition(moduleKey)?.module.name ?? module?.module.name ?? { en: moduleKey }) : moduleName;
  const pageName = child ? strings.entity(child.key, 'name', child.name) : moduleName;
  const title = forms ? (rt ? t('modSettings.form.title', { name: formSingular(strings, rt) }) : '') : pageName;
  const wsName = me?.workspaces.find((w) => w.id === ws)?.name ?? '';
  // Перекладені підписи — залежності мемо (а не `t`, чия тотожність не змінюється, коли мова встановлюється після першого рендеру).
  const sectionLabel = t(forms ? 'modSettings.ctx.forms' : global ? 'modSettings.ctx.catalog' : 'modSettings.ctx.modules');
  const customize = t('modSettings.ctx.customize');
  const backLabel = t(forms ? 'modSettings.ctx.backForms' : 'modSettings.ctx.backModules');
  const crumbs = useMemo(
    () => customizeCrumbs({ id: ws, name: wsName }, customize, { key: section, label: sectionLabel }, title),
    [ws, wsName, customize, section, sectionLabel, title],
  );
  const links = useMemo<ISettingsLinks>(
    () => ({ backLabel, backTo: routes.customize(section), formTo: routes.customizeForm(moduleKey, child?.key) }),
    [backLabel, section, moduleKey, child],
  );
  return { forms, rt, child, focus: child ? undefined : params.get('focus') || undefined, formName, title, crumbs, links };
}
